import React, { useEffect, useState } from 'react';

/**
 * Analytics - A React component for tracking website traffic
 * 
 * @param {Object} props
 * @param {string} props.site - The domain of the site (e.g., "example.com")
 * @param {string} props.apiUrl - The URL of the tracking API (defaults to production URL)
 * @param {string} props.apiKey - The API key for authentication (required)
 * @param {boolean} props.trackScroll - Whether to track scroll percentage (default: false)
 * @param {boolean} props.trackIp - Whether to track IP address (default: false)
 * @param {boolean} props.debug - Whether to log debug information (default: false)
 * @returns {null} - This component doesn't render anything
 */
const Analytics = ({
  site,
  apiUrl = 'https://orbitertracker.onrender.com/api/track',
  apiKey,
  trackScroll = false,
  trackIp = false,
  debug = false
}) => {
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [ipAddress, setIpAddress] = useState(null);
  
  // Log debug information
  const logDebug = (message) => {
    if (debug) {
      console.log(`[Analytics] ${message}`);
    }
  };

  // Track page view on component mount
  useEffect(() => {
    if (!site) {
      console.error('[Analytics] Error: "site" prop is required');
      return;
    }

    // Get device type
    const getDeviceType = () => {
      const userAgent = navigator.userAgent;
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
        return 'tablet';
      }
      if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
        return 'mobile';
      }
      return 'desktop';
    };

    // Get IP address if trackIp is enabled
    const getIpAddress = async () => {
      if (trackIp) {
        try {
          const response = await fetch('https://api.ipify.org?format=json');
          const data = await response.json();
          setIpAddress(data.ip);
          logDebug(`IP Address: ${data.ip}`);
        } catch (error) {
          console.error('[Analytics] Error fetching IP address:', error);
        }
      }
    };

    // Track page view
    const trackPageView = async () => {
      try {
        const eventData = {
          site,
          path: window.location.pathname,
          referrer: document.referrer || null,
          device: getDeviceType(),
          timestamp: new Date().toISOString(),
        };

        // Add optional data if available
        if (trackIp && ipAddress) {
          eventData.ipAddress = ipAddress;
        }
        
        if (trackScroll) {
          eventData.scrollPercentage = scrollPercentage;
        }

        logDebug(`Tracking page view: ${JSON.stringify(eventData)}`);

        // Validate API key is provided
        if (!apiKey) {
          console.error('[Analytics] Error: "apiKey" prop is required');
          return;
        }
        
        // Send data to tracking API
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey
          },
          body: JSON.stringify(eventData),
          // Use no-cors mode if needed for cross-origin requests
          // mode: 'no-cors',
        });

        if (response.ok) {
          logDebug('Page view tracked successfully');
        } else {
          console.error('[Analytics] Error tracking page view:', await response.text());
        }
      } catch (error) {
        console.error('[Analytics] Error tracking page view:', error);
      }
    };

    // Initialize tracking
    getIpAddress().then(() => {
      trackPageView();
    });

    // Clean up
    return () => {
      // Any cleanup if needed
    };
  }, [site, apiUrl, trackIp, ipAddress]);

  // Track scroll percentage if enabled
  useEffect(() => {
    if (!trackScroll) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollTop / docHeight * 100;
      setScrollPercentage(Math.round(scrollPercent));
      logDebug(`Scroll percentage: ${Math.round(scrollPercent)}%`);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [trackScroll, debug]);

  // This component doesn't render anything
  return null;
};

export default Analytics;
